import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Bell, CheckCircle2, ChevronDown, ChevronUp, Paperclip, Settings2, Trash2 } from 'lucide-react';
import { NotificationSettingsMatrix } from '@/components/NotificationSettingsMatrix';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { formatDateLabel } from '@/lib/format';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { useAuthStore, hasPermission } from '@/stores/auth';
import { usePermsReady } from '@/hooks/useEffectivePermissions';
import { ConditionSetEditor } from '@/components/conditions/ConditionSetEditor';
import type { ConditionCatalog, ConditionSetsResponse } from '@/components/conditions/types';
import { DOW_LABELS } from '@/lib/calendar-day';
import {
  buildWorkflowTypeByWorkflowId, resolveEffectiveMaxLevels, resolveDefaultPatternWorkflowId, formatMaxLevels,
  type WorkflowSetting, type WorkflowPatternRow,
} from '@/lib/approval-pattern-selection';

/**
 * `/approvals` (v0.4 章⑤ F-5、新規の承認受信箱)。
 * 設計: docs/design/v04-shift-labor-management-detail.md §5-8 / §5-10
 *
 * ⚠️ この画面は「自分が承認者になっている申請」の受信箱であり、`/settings/approvals`
 * (ApprovalRuleBuilderPage、承認者の*ルール定義*画面) とは別物 (§0-J-2)。
 * ⚠️ D-113: 矛盾は「承認の瞬間に見せる」ことが要点。ブロックはしない (§5-17-1 分類B)。
 */

type ApprovalRow = {
  request_id: string;
  workflow_id: string;
  workflow_type: string;
  requester_employee_id: string | null;
  requester_name: string | null;
  target_node_id: string | null;
  target_node_name: string | null;
  payload: Record<string, unknown>;
  current_level: number;
  max_levels: number;
  status: string;
  created_at: string;
  possible_conflict: boolean;
  conflict_reason: string | null;
  // 使い勝手フィードバック⑤a (D-U17): 申請インスタンス由来の行にのみ入る。null = 差し戻し非対応。
  rejection_behavior: 'reject_only' | 'return_only' | 'both' | null;
  // 使い勝手フィードバック増分⑧ (D-U31): 承認者から見える (可視性レベル 'full'/'exists_only' の)
  // 添付件数。申請インスタンス由来でない行、file 型項目が無い申請種別では常に0。
  attachment_count: number;
};

const WORKFLOW_TYPE_LABELS: Record<string, string> = {
  import_commit: 'データ取込コミット',
  standing_pattern_change: '恒常パターンの変更',
  shift_post_confirm_change: '確定後のシフト変更',
  shift_preference_amendment: '締切後の希望修正',
  inheritable_setting_override: '階層継承設定の上書き',
  // TD-27 (承認ワークフロー受信者モデル再設計、§9-5): 休暇管理3ワークフローが未登録だったため追加。
  leave_request: '休暇申請の決定',
  leave_absence: '期間型休業の決定',
  leave_grant_adjust: '休暇残数の調整',
  // TD-28 段階④ (migration 0092): 汎用申請の承認。⚠️ 追加を忘れると承認設定画面・承認受信箱に
  // workflow_type の生コードがそのまま表示される (§8-55/§8-93、TD-27 でも同じ漏れを直した)。
  overtime_request: '残業の事前許可',
  personal_info_change: '個人情報の変更申請',
};

// TD-27: 承認段階の受信者 (承認者の条件ベース指定 + watcher(共有・通知専用の宛先))。
const APPROVAL_RECIPIENT_USAGE_POINT_ID = 'approval_recipient';

const CHANGE_TYPE_LABELS: Record<string, string> = {
  reduce: '削減 (時間短縮・取消)', increase: '増員・延長', swap: '店舗変更',
};

/** payload の中身は workflow_type ごとに形が異なるため、要点だけを日本語で組み立てる。
 *  ⚠️ フル解決 (店舗名・氏名の深い解決) はせず、GET /approvals が既に付与した
 *  requester_name/target_node_name のみを使う (過剰設計をしない、§6d)。 */
function describePayload(row: ApprovalRow): string {
  const p = row.payload;
  if (row.workflow_type === 'standing_pattern_change') {
    // v0.4 追加要件⑫ 増分⑤ (security-reviewer指摘 High): 対象店舗の shift_preference:view を
    // 持たない決裁者には payload 全体がマスクされる (routes/approvals.ts maskStandingPatternChangePayload)。
    if (p.masked === true) return `${row.target_node_name ?? '店舗'} の週間カバレッジの変更 (閲覧権限がないため詳細は表示されません)`;
    const entries = Array.isArray(p.entries) ? p.entries.length : 0;
    return `${row.target_node_name ?? '店舗'} の週間カバレッジを ${entries} 枠に変更`;
  }
  if (row.workflow_type === 'shift_post_confirm_change') {
    const kind = p.kind === 'cancel' ? '取消' : (typeof p.change === 'object' && p.change && 'changeType' in p.change
      ? CHANGE_TYPE_LABELS[String((p.change as { changeType?: string }).changeType)] ?? '変更'
      : '変更');
    return `${row.target_node_name ?? '店舗'} のシフト割当を${kind}`;
  }
  if (row.workflow_type === 'import_commit') {
    return 'CSV取込データのコミット';
  }
  // TD-28: 汎用申請。payload は {request_instance_id} の参照のみを持つ (§8) ため、
  // 内容は申請詳細画面で確認する (下の「申請内容を確認」リンク)。
  if (typeof p.request_instance_id === 'string') return '申請の承認';
  return JSON.stringify(p).slice(0, 80);
}

/** TD-28: 承認対象が汎用申請なら request_instance_id を返す (承認画面から詳細へ導くため)。 */
function requestInstanceIdOf(row: ApprovalRow): string | null {
  const v = row.payload.request_instance_id;
  return typeof v === 'string' && v ? v : null;
}

// v0.4 追加要件⑫ 増分⑤ (D-381、-detail.md §3-2): standing_pattern_change の差分・影響表示。
// ⚠️ D-113 の対象外 (店舗全員の週間グリッドを丸ごと置換する申請のため、差分表示が無いと
// 承認者が「何を承認したのか」を知る手段が原理的に無い)。
type StandingPatternPersonDiff = {
  employee_id: string | null; external_person_id: string | null; display_name: string;
  added: Array<{ day_of_week: number; start_time: string; end_time: string; preference_level_id: string }>;
  removed: Array<{ day_of_week: number; start_time: string; end_time: string }>;
};
type StandingPatternDiffSummary = { changed_person_count: number; persons: StandingPatternPersonDiff[] };
type StandingPatternContractedImpact = {
  employee_id: string; before: number | null; after: number | null;
  flex_slots_before: number | null; flex_slots_after: number | null;
};
type StandingPatternStaffingImpact = {
  day_of_week: number; representative_date: string | null;
  satisfied_before: boolean | null; satisfied_after: boolean | null;
};
type StandingPatternImpact = { contracted: StandingPatternContractedImpact[]; staffing_by_dow: StandingPatternStaffingImpact[] };

/** payload.diff_summary/impact_summary は①旧形式の申請 (D-385 の後方互換対象)、
 *  ②閲覧権限が無い決裁者向けにマスクされた申請 (routes/approvals.ts の
 *  maskStandingPatternChangePayload) のいずれかで存在しない/一部欠けることがある。
 *  型ガード付きで安全に取り出す (存在しなければ null、画面側で差分セクション自体を表示しない)。
 *  ⚠️ QAレビュー指摘 (Medium): persons[].added/removed が配列であることまでは検証していなかった
 *  ため、不正な形状の payload で .map() が例外を投げモーダル全体がクラッシュしうる。各要素の
 *  added/removed を Array.isArray で個別に検証し、配列でなければ空配列にフォールバックする。 */
function standingPatternDiffOf(row: ApprovalRow): { diff: StandingPatternDiffSummary; impact: StandingPatternImpact } | null {
  if (row.workflow_type !== 'standing_pattern_change') return null;
  const rawDiff = row.payload.diff_summary;
  if (!rawDiff || typeof rawDiff !== 'object' || !Array.isArray((rawDiff as { persons?: unknown }).persons)) return null;
  const persons = ((rawDiff as { persons: unknown[] }).persons)
    .filter((p): p is Record<string, unknown> => !!p && typeof p === 'object')
    .map((p) => ({
      employee_id: typeof p.employee_id === 'string' ? p.employee_id : null,
      external_person_id: typeof p.external_person_id === 'string' ? p.external_person_id : null,
      display_name: typeof p.display_name === 'string' ? p.display_name : '',
      added: Array.isArray(p.added) ? p.added : [],
      removed: Array.isArray(p.removed) ? p.removed : [],
    })) as StandingPatternPersonDiff[];
  const diff: StandingPatternDiffSummary = {
    changed_person_count: typeof (rawDiff as { changed_person_count?: unknown }).changed_person_count === 'number'
      ? (rawDiff as { changed_person_count: number }).changed_person_count
      : persons.length,
    persons,
  };
  const rawImpact = row.payload.impact_summary;
  const contracted = rawImpact && typeof rawImpact === 'object' && Array.isArray((rawImpact as { contracted?: unknown }).contracted)
    ? (rawImpact as StandingPatternImpact).contracted
    : [];
  const staffingByDow = rawImpact && typeof rawImpact === 'object' && Array.isArray((rawImpact as { staffing_by_dow?: unknown }).staffing_by_dow)
    ? (rawImpact as StandingPatternImpact).staffing_by_dow
    : [];
  return { diff, impact: { contracted, staffing_by_dow: staffingByDow } };
}

/** v0.4 追加要件⑫ 増分⑤ (design-expert/design-and-code レビュー指摘、CRITICAL/MEDIUM):
 *  satisfied は null (代表日が選定できず評価不能) をとりうるが `=== false` の三項演算子だと
 *  null が「充足」と誤表示されていた。ShiftStandingPatternsPage.tsx の同型修正と同じ表示規約に揃える。 */
function satisfiedLabel(satisfied: boolean | null): { text: string; className: string } {
  if (satisfied === null) return { text: '判定不能', className: 'text-text-sub' };
  if (satisfied === false) return { text: '不足', className: 'text-alert-text' };
  return { text: '充足', className: '' };
}

function DecideModal({ row, onClose, onDone }: { row: ApprovalRow; onClose: () => void; onDone: () => void }) {
  const [comment, setComment] = useState('');
  // v0.4 追加要件⑫ 増分⑤ (D-381): standing_pattern_change は D-113 の対象外のため、
  // 差分・影響を編集画面を開かずにここで確認できるようにする。
  const standingPatternDiff = standingPatternDiffOf(row);
  // 使い勝手フィードバック⑤a (D-U17): 差し戻す/却下するボタンの出し分け。
  const canReturn = row.rejection_behavior === 'return_only' || row.rejection_behavior === 'both';
  const canReject = row.rejection_behavior !== 'return_only';
  const decideMut = useMutation({
    mutationFn: (decision: 'approve' | 'reject' | 'return') => apiFetch<{ status: string; commit_status: string | null }>(
      `/approvals/${row.request_id}/decide`,
      { method: 'POST', body: JSON.stringify({ decision, comment: comment.trim() || null }) },
    ),
    onSuccess: (res, decision) => {
      if (res.commit_status === 'failed') {
        toast.error('承認は記録されましたが、適用に失敗しました (対象が既に変更されている可能性があります)');
      } else if (decision === 'return') {
        // ⚠️ ブラウザ実機検証で発見: 前段階へ戻す (⑤b、target.kind==='level') 場合、
        // レスポンスの status は次の承認者へ進んだ通常ケースと同じ 'pending' になるため、
        // res.status だけで分岐すると「次の承認者へ進みました」という前進を示す文言になり、
        // 実際には差し戻し (後退) したことと矛盾する。decision 自体が 'return' なら常に
        // 差し戻し系の文言にする。
        toast.success(res.status === 'returned' ? '差し戻しました' : '前の承認段階へ差し戻しました');
      } else {
        toast.success(
          res.status === 'approved' ? '承認しました'
            : res.status === 'rejected' ? '却下しました'
              : '次の承認者へ進みました',
        );
      }
      onDone();
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '処理に失敗しました'),
  });

  return (
    <Modal
      open title="申請の承認/却下" onClose={onClose} size={standingPatternDiff ? 'md' : 'sm'}
      footer={(
        // 使い勝手フィードバック⑤a (B5-02、詳細設計 §6-7): <480px (実質 sm 未満、CLAUDE.md §9 の
        // 完了条件マトリクス 320/375/768/1280 のいずれでも sm(640px) 基準と同一の見え方になる) では
        // 縦積みにし「承認/差し戻す/却下/キャンセル」の順に固定、sm 以上では従来どおりの横並びの
        // 右詰め (承認が最も右=最終アクション) にする。`sm:contents` で Modal 側の
        // flex-wrap justify-end を sm 以上でそのまま子に効かせ、order-* で並び順だけ変える。
        <div className="flex flex-col sm:contents w-full gap-2">
          <Button
            variant="secondary" onClick={onClose} disabled={decideMut.isPending}
            className="order-4 sm:order-1 w-full sm:w-auto"
          >
            キャンセル
          </Button>
          {canReject && (
            // design-expertレビュー反映 (増分⑤a): danger と primary は同一の塗り赤スタイルのため
            // 承認 (primary) と並べると区別が付かなかった (§2.2「赤は1画面1箇所」にも抵触)。
            // 却下は輪郭の danger-outline にし、承認だけを画面で唯一の強調色にする。
            <Button
              variant="danger-outline" loading={decideMut.isPending && decideMut.variables === 'reject'}
              disabled={decideMut.isPending || !comment.trim()}
              onClick={() => decideMut.mutate('reject')}
              className="order-3 sm:order-2 w-full sm:w-auto"
            >
              却下する
            </Button>
          )}
          {canReturn && (
            <Button
              variant="secondary" loading={decideMut.isPending && decideMut.variables === 'return'}
              disabled={decideMut.isPending || !comment.trim()}
              onClick={() => decideMut.mutate('return')}
              className="order-2 sm:order-3 w-full sm:w-auto"
            >
              差し戻す
            </Button>
          )}
          <Button
            variant="primary" loading={decideMut.isPending && decideMut.variables === 'approve'}
            disabled={decideMut.isPending}
            onClick={() => decideMut.mutate('approve')}
            className="order-1 sm:order-4 w-full sm:w-auto"
          >
            承認する
          </Button>
        </div>
      )}
    >
      <div className="space-y-3">
        <p className="text-sm text-text-body">{describePayload(row)}</p>
        {/* v0.4 追加要件⑫ 増分⑤ (D-381、-detail.md §3-2): standing_pattern_change の差分・影響。
            D-113 の対象外 (店舗全員の週間グリッドを丸ごと置換する申請のため、差分表示が無いと
            承認者が「何を承認したのか」を知る手段が原理的に無い)。旧形式の申請 (D-385 の後方互換
            対象) には diff_summary が無いため、その場合は何も表示しない。 */}
        {standingPatternDiff && (
          <div className="space-y-2 border border-surface-border rounded p-2 bg-surface-sunken">
            {standingPatternDiff.diff.persons.length === 0 ? (
              <p className="text-xs text-text-sub">変更点はありません。</p>
            ) : (
              <>
                <p className="text-xs text-text-sub">{standingPatternDiff.diff.changed_person_count}名の週間の枠が変わります。</p>
                <ul className="space-y-1.5 max-h-48 overflow-y-auto">
                  {standingPatternDiff.diff.persons.map((p) => (
                    <li key={p.employee_id ? `E:${p.employee_id}` : `X:${p.external_person_id ?? ''}`} className="border border-surface-line rounded p-2 bg-white">
                      <p className="font-medium text-text-body text-sm">{p.display_name}</p>
                      {p.added.length > 0 && (
                        <p className="text-xs text-text-body mt-0.5">
                          追加: {p.added.map((a, i) => (
                            <span key={i}>{i > 0 && '、'}{DOW_LABELS[a.day_of_week]}曜日 {a.start_time}〜{a.end_time}</span>
                          ))}
                        </p>
                      )}
                      {p.removed.length > 0 && (
                        <p className="text-xs text-text-sub mt-0.5">
                          削除: {p.removed.map((r, i) => (
                            <span key={i}>{i > 0 && '、'}{DOW_LABELS[r.day_of_week]}曜日 {r.start_time}〜{r.end_time}</span>
                          ))}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              </>
            )}
            {standingPatternDiff.impact.contracted.length > 0 && (
              <div className="space-y-1 pt-1 border-t border-surface-border">
                <p className="text-xs font-semibold text-text-sub">充足・融通枠への影響</p>
                <ul className="space-y-0.5">
                  {standingPatternDiff.impact.contracted.map((ct) => {
                    const person = standingPatternDiff.diff.persons.find((p) => p.employee_id === ct.employee_id);
                    return (
                      <li key={ct.employee_id} className="text-xs text-text-body">
                        {person?.display_name ?? ct.employee_id}: 充足 {ct.before == null ? '—' : `${Math.round(ct.before * 100)}%`} → {ct.after == null ? '—' : `${Math.round(ct.after * 100)}%`}
                        {'　'}融通枠 {ct.flex_slots_before ?? '—'} → {ct.flex_slots_after ?? '—'}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            {standingPatternDiff.impact.staffing_by_dow.length > 0 && (
              <div className="space-y-1 pt-1 border-t border-surface-border">
                <p className="text-xs font-semibold text-text-sub">必要人員への影響 (代表日での近似)</p>
                <ul className="space-y-0.5">
                  {standingPatternDiff.impact.staffing_by_dow.map((d) => {
                    const before = satisfiedLabel(d.satisfied_before);
                    const after = satisfiedLabel(d.satisfied_after);
                    return (
                      <li key={d.day_of_week} className="text-xs text-text-body">
                        {DOW_LABELS[d.day_of_week]}曜日:{' '}
                        <span className={before.className}>{before.text}</span>
                        {' → '}
                        <span className={`${after.className} ${d.satisfied_after === false ? 'font-semibold' : ''}`}>{after.text}</span>
                        {d.representative_date && <span className="text-text-sub"> (代表日 {d.representative_date})</span>}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        )}
        {/* ⚠️ TD-28 D-25 必須要件①: 承認者は申請の中身を見た上で決裁する必要がある。
            承認画面は payload に {request_instance_id} しか持たない (§8) ため、詳細への導線を出す。
            詳細側で「この段階では表示されない項目があります」が表示される。 */}
        {requestInstanceIdOf(row) && (
          <div className="rounded border border-surface-border bg-surface-sunken px-3 py-2">
            <Link to={`/requests?open=${requestInstanceIdOf(row)}`}
              className="text-sm text-misespark-navy underline min-h-[44px] sm:min-h-0 inline-flex items-center">
              申請内容を確認する
            </Link>
            <p className="text-xs text-text-sub mt-0.5">
              申請の各項目は、設定によってこの承認段階では表示されない場合があります。
            </p>
          </div>
        )}
        {/* 使い勝手フィードバック増分⑧ (D-U31、design-expert MEDIUM-1指摘反映): Office 文書等は
            ブラウザでプレビューできずダウンロード前提のため、中身を見ないまま決裁されることを
            防ぐ目的の注意喚起。他の補足文言に埋没させず、possible_conflict と同じ「独立した
            アイコン付きボックス」の視覚的重みを与える (警告ではないため alert 色は使わない)。 */}
        {row.attachment_count > 0 && (
          <div className="rounded border border-surface-border bg-surface-sunken px-3 py-2 flex items-start gap-2">
            <Paperclip size={16} className="text-chrome-base shrink-0 mt-0.5" aria-hidden />
            <p className="text-sm text-text-body">添付 {row.attachment_count} 件（要ダウンロード）。内容を確認してから決裁してください。</p>
          </div>
        )}
        {row.possible_conflict && (
          <div className="rounded border border-alert-border bg-alert-bg px-3 py-2 flex items-start gap-2">
            <AlertTriangle size={16} className="text-alert-text shrink-0 mt-0.5" aria-hidden />
            <p className="text-sm text-alert-text">{row.conflict_reason ?? '状況が申請時点から変わっている可能性があります。'}</p>
          </div>
        )}
        <div>
          <label htmlFor="approval-comment" className="block text-xs text-text-sub mb-1">
            コメント ({canReturn ? '却下・差し戻し時は入力必須' : '却下時は入力必須'})
          </label>
          <textarea
            id="approval-comment" value={comment} onChange={(e) => setComment(e.target.value)} rows={3}
            className="w-full px-3 py-2 rounded border border-surface-border focus-ring text-sm"
            placeholder={canReturn ? '却下理由・差し戻し理由や申し送り事項があれば入力してください' : '却下理由や申し送り事項があれば入力してください'}
          />
        </div>
      </div>
    </Modal>
  );
}

// D-U22 (増分①): WorkflowSetting/WorkflowPatternRow は lib/approval-pattern-selection.ts からの
// re-export (workflow_type ごとの全パターン一覧・段階選択ロジックと型を共有するため単一の場所に置く)。
type WorkflowPatternUsage = {
  pending_request_count: number; bound_node_count: number; bound_request_type_count: number; max_pending_level: number;
};
type Override = {
  override_id: string; workflow_id: string; request_id: string | null; node_id: string | null;
  level: number; assignee_employee_id: string; persistence: string; created_at: string;
};
type Employee = { employee_id: string; employee_number: string; family_name: string; given_name: string };
// TD-27: 承認段階の受信者 (承認者の条件ベース指定 + watcher)
type Recipient = {
  recipient_id: string; workflow_id: string; node_id: string | null; level: number;
  recipient_role: 'approver' | 'watcher'; resolution_mode: 'explicit_employee' | 'condition_set';
  assignee_employee_id: string | null; condition_set_id: string | null;
  persistence: string | null; label: string | null; created_at: string;
};
type RecipientForm = {
  // D-U22 (増分①): workflowId は「対象パターン」の指定 (省略時はその workflow_type の既定パターン)
  workflowType: string; workflowId: string; level: number; recipientRole: 'approver' | 'watcher';
  resolutionMode: 'explicit_employee' | 'condition_set';
  employeeId: string; persistence: string; conditionSetId: string; label: string;
};

/** 承認ワークフローの on/off + 承認者上書き (D-97)。all_stores スコープの `approval_workflow:manage`
 *  保有者のみに表示する (バックエンドが同スコープを要求、フロント側も同じ基準で出し分ける)。 */
function WorkflowSettingsPanel() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  // ⚠️ QA指摘反映: バックエンド (GET/PUT /approvals/workflows, /assignee-overrides) は
  // approval_workflow:manage の all_stores スコープを要求する。コメントの意図通りフロント側にも
  // 同じ基準のガードを置く (無いとパート従業員にもボタンが見え、押すと403になるだけの不整合だった)。
  const canManageWorkflows = hasPermission(user?.effective_permissions, 'approval_workflow', 'manage');
  const permsReady = usePermsReady();
  const [managing, setManaging] = useState(false);
  // ⚠️ design-and-code HIGH指摘反映 (D-215/D-216): level が常に1固定だと、既定ONのstanding_pattern_change
  // (max_levels=3、migration 0053) は level=2 に進んだ瞬間に承認者0人で永久停止していた
  // (バックエンド側でも空レベルの自動スキップを実装したが、そもそも level 2/3 の承認者を
  // 設定する手段がUIに無ければ運用上使われない)。level 選択肢を追加する。
  // D-U22 (増分①): workflowId は「対象パターン」の指定 (省略時はその workflow_type の既定パターン)
  const [overrideForm, setOverrideForm] = useState<{ workflowType: string; workflowId: string; employeeId: string; persistence: string; level: number } | null>(null);
  // TD-27: 承認段階の受信者 (承認者の条件ベース指定 + watcher)
  const [recipientsWorkflowType, setRecipientsWorkflowType] = useState('');
  // D-U22 (増分①): 受信者一覧の絞り込み対象パターン (workflow_type 内の特定パターン、省略時は既定パターン)
  const [recipientsWorkflowId, setRecipientsWorkflowId] = useState('');
  const [recipientForm, setRecipientForm] = useState<RecipientForm | null>(null);
  const [showConditionEditor, setShowConditionEditor] = useState(false);
  // D-U22 (増分①): 承認パターンの段階数編集・新規作成・削除
  const [editingPattern, setEditingPattern] = useState<{ workflowId: string; workflowType: string; label: string; maxLevels: number } | null>(null);
  const [deletingPattern, setDeletingPattern] = useState<WorkflowPatternRow | null>(null);
  const [creatingPatternForType, setCreatingPatternForType] = useState<string | null>(null);
  const [createPatternForm, setCreatePatternForm] = useState<{ code: string; label: string; maxLevels: number }>({ code: '', label: '', maxLevels: 1 });
  // 使い勝手フィードバック⑥.5 (D-U42/D-U45): ②層 (承認パターン単位) の通知設定
  const [notificationsPattern, setNotificationsPattern] = useState<WorkflowPatternRow | null>(null);

  const { data: workflows = [] } = useQuery<WorkflowSetting[]>({
    queryKey: ['approval-workflows'], queryFn: () => apiFetch<WorkflowSetting[]>('/approvals/workflows'), enabled: managing,
  });
  // D-U22 (増分①): workflow_type ごとの全パターン一覧 (code='default' 含む)。§8-89 型の懸念には
  // 当たらない (workflow_type は高々10種前後の固定カタログ、useShiftPatternPresets と同じ思想)。
  const patternQueries = useQueries({
    queries: workflows.map((w) => ({
      queryKey: ['workflow-patterns', w.workflow_type],
      queryFn: () => apiFetch<WorkflowPatternRow[]>(`/approvals/workflow-patterns?workflow_type=${encodeURIComponent(w.workflow_type)}`),
      enabled: managing,
    })),
  });
  const patternsByType = new Map<string, WorkflowPatternRow[]>(workflows.map((w, i) => [w.workflow_type, patternQueries[i]?.data ?? []]));
  // ⚠️ D-U22 §2-2: GET /approvals/workflows は code='default' のみ返すため、override/recipient の
  // 一覧が (追加パターンを含む) 任意の workflow_id を workflow_type ラベルへ引けるよう、
  // 全パターンを走査した完全なマップを別途持つ (ApprovalsPage.tsx の override 一覧ラベル解決の修正)
  const workflowTypeByWorkflowId = buildWorkflowTypeByWorkflowId(patternsByType);
  // D-U22 §2-2: 段階の選択肢は「選択中のパターン」の max_levels に従う (workflows は既定パターンの
  // 値しか持たないため、追加パターンでは食い違う)。override/recipient 両フォームで同じ解決ロジックを使う。
  const overridePatterns = overrideForm ? (patternsByType.get(overrideForm.workflowType) ?? []) : [];
  const overrideMaxLevels = overrideForm
    ? resolveEffectiveMaxLevels(overridePatterns, overrideForm.workflowId, workflows, overrideForm.workflowType) : 1;
  const recipientFormPatterns = recipientForm ? (patternsByType.get(recipientForm.workflowType) ?? []) : [];
  const recipientFormMaxLevels = recipientForm
    ? resolveEffectiveMaxLevels(recipientFormPatterns, recipientForm.workflowId, workflows, recipientForm.workflowType) : 1;
  const usagePatternId = editingPattern?.workflowId ?? deletingPattern?.workflow_id ?? '';
  const { data: patternUsage } = useQuery<WorkflowPatternUsage>({
    queryKey: ['workflow-pattern-usage', usagePatternId],
    queryFn: () => apiFetch<WorkflowPatternUsage>(`/approvals/workflow-patterns/${usagePatternId}/usage`),
    enabled: managing && !!usagePatternId,
  });
  const { data: overrides = [] } = useQuery<Override[]>({
    queryKey: ['approval-assignee-overrides'], queryFn: () => apiFetch<Override[]>('/approvals/assignee-overrides'), enabled: managing,
  });
  // ⚠️ Testレビュー指摘反映: GET /employees は isCompanyAdmin ロール判定 (permissionベースではない)
  // のため、approval_workflow:manage を持つが company_admin ロールではないカスタムロールでは
  // 403 になりうる。isError を無視すると社員選択の Select が無言で空になるため明示的に扱う。
  const { data: employees = [], isError: employeesError } = useQuery<Employee[]>({
    queryKey: ['employees'], queryFn: () => apiFetch<Employee[]>('/employees'), enabled: managing, retry: false,
  });
  // TD-27: 承認段階の受信者 (承認者の条件ベース指定 + watcher)。
  // ⚠️ D-U22 §2-2: workflow_id (対象パターン) が選ばれていればそちらを優先する。
  // 未選択なら従来どおり workflow_type (=既定パターン) で絞り込む。
  const { data: recipients = [] } = useQuery<Recipient[]>({
    queryKey: ['approval-recipients', recipientsWorkflowType, recipientsWorkflowId],
    queryFn: () => apiFetch<Recipient[]>(recipientsWorkflowId
      ? `/approvals/recipients?workflow_id=${encodeURIComponent(recipientsWorkflowId)}`
      : `/approvals/recipients?workflow_type=${encodeURIComponent(recipientsWorkflowType)}`),
    enabled: managing && !!recipientsWorkflowType,
  });
  const recipientsPatterns = patternsByType.get(recipientsWorkflowType) ?? [];
  const { data: conditionCatalog } = useQuery<ConditionCatalog>({
    queryKey: ['condition-catalog'], queryFn: () => apiFetch<ConditionCatalog>('/condition-catalog'),
    enabled: managing, staleTime: 5 * 60 * 1000,
  });
  const { data: recipientConditionSetsData } = useQuery<ConditionSetsResponse>({
    queryKey: ['condition-sets', APPROVAL_RECIPIENT_USAGE_POINT_ID],
    queryFn: () => apiFetch<ConditionSetsResponse>(`/condition-sets?usage_point_id=${APPROVAL_RECIPIENT_USAGE_POINT_ID}`),
    enabled: managing,
  });
  const recipientConditionSets = recipientConditionSetsData?.sets ?? [];
  const attributeTypesById = new Map((conditionCatalog?.attribute_types ?? []).map((t) => [t.attribute_type_id, t]));
  const recipientUsagePoint = conditionCatalog?.usage_points.find((u) => u.usage_point_id === APPROVAL_RECIPIENT_USAGE_POINT_ID);

  const toggleMut = useMutation({
    mutationFn: (params: { workflowType: string; isActive: boolean }) => apiFetch(`/approvals/workflows/${params.workflowType}/active`, {
      method: 'PUT', body: JSON.stringify({ is_active: params.isActive }),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['approval-workflows'] }); toast.success('設定を更新しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '更新に失敗しました'),
  });

  const createOverrideMut = useMutation({
    // ⚠️ D-U22 §2-2: workflow_id (選択されたパターン) を優先して送る。未選択 (旧データ由来) は
    // workflow_type にフォールバックし、その型の既定パターンへ登録する (従来どおりの挙動)。
    mutationFn: (params: { workflowType: string; workflowId: string; employeeId: string; persistence: string; level: number }) => apiFetch('/approvals/assignee-overrides', {
      method: 'POST',
      body: JSON.stringify({
        ...(params.workflowId ? { workflow_id: params.workflowId } : { workflow_type: params.workflowType }),
        node_id: null, level: params.level,
        assignee_employee_id: params.employeeId, persistence: params.persistence,
      }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['approval-assignee-overrides'] });
      setOverrideForm(null);
      toast.success('承認者の指名を登録しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '登録に失敗しました'),
  });

  const revokeOverrideMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/approvals/assignee-overrides/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['approval-assignee-overrides'] }); toast.success('指名を解除しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '解除に失敗しました'),
  });

  // D-U22 (増分①): 承認パターンの段階数編集・新規作成・削除
  const updatePatternMut = useMutation({
    mutationFn: (params: { workflowId: string; label: string; maxLevels: number }) => apiFetch(`/approvals/workflow-patterns/${params.workflowId}`, {
      method: 'PUT', body: JSON.stringify({ label: params.label, max_levels: params.maxLevels }),
    }),
    onSuccess: () => {
      // ⚠️ GET /workflows と GET /workflow-patterns は同じテーブルを別の絞り込みで読むため、
      // 両方を無効化しないと override/recipient 作成フォームの段階選択が古い max_levels のまま残る。
      qc.invalidateQueries({ queryKey: ['approval-workflows'] });
      qc.invalidateQueries({ queryKey: ['workflow-patterns'] });
      setEditingPattern(null);
      toast.success('パターンを更新しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '更新に失敗しました'),
  });

  const deletePatternMut = useMutation({
    mutationFn: (workflowId: string) => apiFetch(`/approvals/workflow-patterns/${workflowId}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['approval-workflows'] });
      qc.invalidateQueries({ queryKey: ['workflow-patterns'] });
      setDeletingPattern(null);
      toast.success('パターンを削除しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'),
  });

  const createPatternMut = useMutation({
    mutationFn: (params: { workflowType: string; code: string; label: string; maxLevels: number }) => apiFetch('/approvals/workflow-patterns', {
      method: 'POST',
      body: JSON.stringify({ workflow_type: params.workflowType, code: params.code, label: params.label, max_levels: params.maxLevels }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workflow-patterns'] });
      setCreatingPatternForType(null);
      toast.success('パターンを作成しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '作成に失敗しました'),
  });

  // TD-27: 承認段階の受信者 (承認者の条件ベース指定 + watcher)
  const createRecipientMut = useMutation({
    // ⚠️ D-U22 §2-2: workflow_id (選択されたパターン) を優先して送る (override と同じ方針)。
    mutationFn: (f: RecipientForm) => apiFetch('/approvals/recipients', {
      method: 'POST',
      body: JSON.stringify({
        ...(f.workflowId ? { workflow_id: f.workflowId } : { workflow_type: f.workflowType }),
        node_id: null, level: f.level,
        recipient_role: f.recipientRole, resolution_mode: f.resolutionMode,
        ...(f.resolutionMode === 'explicit_employee'
          ? { assignee_employee_id: f.employeeId, persistence: f.persistence }
          : { condition_set_id: f.conditionSetId }),
        label: f.label.trim() || undefined,
      }),
    }),
    onSuccess: () => {
      // ⚠️ queryKey に recipientsWorkflowId が加わったため部分一致 (['approval-recipients']) で無効化する
      qc.invalidateQueries({ queryKey: ['approval-recipients'] });
      setRecipientForm(null);
      toast.success('受信者設定を登録しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '登録に失敗しました'),
  });

  const revokeRecipientMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/approvals/recipients/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['approval-recipients'] }); toast.success('受信者設定を解除しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '解除に失敗しました'),
  });

  // ⚠️ §8-76 対策: 早期returnは全hooksの宣言が終わった後に置く (途中に挟むと
  // effective_permissions が非同期に届いた際の再レンダリングで hooks 呼び出し数が変わりクラッシュする)
  if (!permsReady) return <div role="status" className="text-text-sub">読み込み中…</div>;
  if (!canManageWorkflows) return null;

  const employeeName = (id: string) => {
    const e = employees.find((x) => x.employee_id === id);
    return e ? `${e.family_name} ${e.given_name}` : id;
  };

  if (!managing) {
    return (
      <div className="flex justify-end">
        <Button variant="secondary" size="sm" leftIcon={<Settings2 size={14} />} onClick={() => setManaging(true)}>
          承認ワークフロー設定
        </Button>
      </div>
    );
  }

  const PERSISTENCE_LABELS: Record<string, string> = { once: '今回のみ', persistent: '恒久', until_reassignment: '異動まで' };

  return (
    <div className="border border-surface-border rounded-lg bg-white p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-text-body">承認ワークフロー設定</h3>
        <Button variant="ghost" size="sm" onClick={() => setManaging(false)}>閉じる</Button>
      </div>
      <div className="space-y-2">
        <p className="text-xs text-text-sub">
          on にするとその種類の変更が申請 → 承認を経由するようになります (D-106、既定は「恒常パターンの変更」のみ ON)。
        </p>
        {/* D-U22 (増分①): workflow_type ごとに全パターン (code='default' を含む) を一覧し、
            段階数・ラベルの編集とパターンの新規作成・削除をここで行う。 */}
        {workflows.map((w) => {
          const patterns = patternsByType.get(w.workflow_type) ?? [];
          return (
            <div key={w.workflow_id} className="py-2 border-t border-surface-border first:border-t-0 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="text-sm font-medium text-text-body">{WORKFLOW_TYPE_LABELS[w.workflow_type] ?? w.workflow_type}</div>
                  <div className="text-xs text-text-sub">{formatMaxLevels(w.max_levels)}</div>
                </div>
                <label className="inline-flex items-center gap-2 text-sm cursor-pointer min-h-[44px]">
                  <input
                    type="checkbox" checked={w.is_active}
                    onChange={(e) => toggleMut.mutate({ workflowType: w.workflow_type, isActive: e.target.checked })}
                    disabled={toggleMut.isPending}
                  />
                  {w.is_active ? '有効' : '無効'}
                </label>
              </div>
              {/* ⚠️ design-expert指摘反映: 375px未満はインデント/罫線を外す (ConditionSetEditor.tsx §2-8
                  と同じ理由 — インデント表現は375pxで潰れる) */}
              <div className="ml-0 sm:ml-3 pl-0 sm:pl-3 border-0 sm:border-l-2 border-surface-border space-y-1">
                {patterns.map((p) => (
                  <div key={p.workflow_id} className="flex items-center justify-between gap-2 py-1 text-xs">
                    {/* ⚠️ design-expert指摘反映からさらに実機確認で発覚: label(最大200文字) と
                        code(最大64文字)+最大段階数を同じ行に並べる (shrink-0) と、狭幅では
                        label 側が幅0まで潰れ文字が一切見えなくなる (truncate は overflow:hidden な
                        flex item の自動最小幅を0にするだけで表示保証はしない)。親の workflow_type 見出し
                        (`text-sm font-medium` + 下に `text-xs text-text-sub`) と同じ2行構成に揃え、
                        ラベルに専用の1行 (truncate) を与えて必ず何文字かは表示されるようにする。 */}
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-text-body truncate" title={p.label}>{p.label}</div>
                      <div className="text-text-sub">({p.code}) {formatMaxLevels(p.max_levels)}</div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {/* ⚠️ design-expert指摘反映: 同ファイル内の他の行アクション (指名を解除/受信者設定を解除) と
                          同じく静的な aria-label にする (動的にラベル全文を埋め込むと最大200文字まで伸びる) */}
                      <button
                        type="button" aria-label="パターンを編集"
                        onClick={() => setEditingPattern({ workflowId: p.workflow_id, workflowType: p.workflow_type, label: p.label, maxLevels: p.max_levels })}
                        className="p-1.5 rounded-md text-text-sub hover:text-text-body hover:bg-surface-sunken min-h-[44px] min-w-[44px] flex items-center justify-center"
                      >
                        <Settings2 size={14} />
                      </button>
                      {/* 使い勝手フィードバック⑥.5 (D-U42/D-U45): ②層 (このパターン単位) の通知設定 */}
                      <button
                        type="button" aria-label="通知設定"
                        onClick={() => setNotificationsPattern(p)}
                        className="p-1.5 rounded-md text-text-sub hover:text-text-body hover:bg-surface-sunken min-h-[44px] min-w-[44px] flex items-center justify-center"
                      >
                        <Bell size={14} />
                      </button>
                      {/* ⚠️ D-U22: code='default' は削除不可 (バックエンドも拒否する)。フロントでも隠す */}
                      {p.code !== 'default' && (
                        <button
                          type="button" aria-label="パターンを削除" onClick={() => setDeletingPattern(p)}
                          className="p-1.5 rounded-md text-text-sub hover:text-danger-text hover:bg-surface-sunken min-h-[44px] min-w-[44px] flex items-center justify-center"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                <Button
                  variant="ghost" size="sm"
                  onClick={() => { setCreatingPatternForType(w.workflow_type); setCreatePatternForm({ code: '', label: '', maxLevels: 1 }); }}
                >
                  パターンを追加
                </Button>
              </div>
            </div>
          );
        })}
      </div>
      <div className="space-y-2 pt-2 border-t border-surface-border">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-bold text-text-sub">承認者の指名 (D-97)</h4>
          <Button
            variant="ghost" size="sm"
            onClick={() => setOverrideForm({
              workflowType: workflows[0]?.workflow_type ?? '', workflowId: workflows[0]?.workflow_id ?? '',
              employeeId: '', persistence: 'persistent', level: 1,
            })}
          >
            指名を追加
          </Button>
        </div>
        {overrides.length === 0 ? (
          <p className="text-xs text-text-sub">指名による上書きはありません (階層/ロールの既定の承認者が使われます)。</p>
        ) : (
          <div className="divide-y divide-surface-border">
            {overrides.map((o) => (
              <div key={o.override_id} className="flex items-center justify-between gap-2 py-2 text-sm">
                <div>
                  <span className="font-medium text-text-body">{employeeName(o.assignee_employee_id)}</span>
                  {/* ⚠️ D-U22 §2-2: workflows (code='default' のみ) ではなく、全パターンを含む
                      workflowTypeByWorkflowId で引く (追加パターンの override でも正しくラベル解決する) */}
                  <span className="text-text-sub ml-2">{WORKFLOW_TYPE_LABELS[workflowTypeByWorkflowId.get(o.workflow_id) ?? ''] ?? o.workflow_id}</span>
                  <span className="text-text-sub ml-2">第{o.level}段階</span>
                  <span className="text-text-sub ml-2">({PERSISTENCE_LABELS[o.persistence] ?? o.persistence})</span>
                </div>
                <button type="button" aria-label="指名を解除" onClick={() => revokeOverrideMut.mutate(o.override_id)}
                  className="p-1.5 rounded-md text-text-sub hover:text-danger-text hover:bg-surface-sunken min-h-[44px] min-w-[44px] flex items-center justify-center">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* TD-27: 承認段階の受信者 (承認者の条件ベース指定 + watcher(共有・通知専用の宛先)) */}
      <div className="space-y-2 pt-2 border-t border-surface-border">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h4 className="text-xs font-bold text-text-sub">受信者設定 (承認者の条件による指定 / 共有・通知先)</h4>
          <Button
            variant="ghost" size="sm"
            onClick={() => {
              const workflowType = recipientsWorkflowType || workflows[0]?.workflow_type || '';
              const workflowId = recipientsWorkflowId || workflows.find((w) => w.workflow_type === workflowType)?.workflow_id || '';
              setRecipientForm({
                workflowType, workflowId,
                level: 1, recipientRole: 'watcher', resolutionMode: 'explicit_employee',
                employeeId: '', persistence: 'persistent', conditionSetId: '', label: '',
              });
            }}
          >
            受信者を追加
          </Button>
        </div>
        <div>
          <label className="block text-xs text-text-sub mb-1">対象ワークフロー</label>
          <Select
            value={recipientsWorkflowType || workflows[0]?.workflow_type || undefined}
            onValueChange={(v) => { setRecipientsWorkflowType(v); setRecipientsWorkflowId(''); }}
          >
            <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="選ぶ" /></SelectTrigger>
            <SelectContent>
              {workflows.map((w) => <SelectItem key={w.workflow_id} value={w.workflow_type}>{WORKFLOW_TYPE_LABELS[w.workflow_type] ?? w.workflow_type}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {/* ⚠️ D-U22 §2-2: この workflow_type に複数パターンがある場合のみ、対象パターンを選べるようにする
            (単一パターンしか無い型では常に既定パターンで一意なため、選択肢を増やして UI を煩雑にしない) */}
        {recipientsPatterns.length > 1 && (
          <div>
            <label className="block text-xs text-text-sub mb-1">対象パターン</label>
            <Select
              value={recipientsWorkflowId || resolveDefaultPatternWorkflowId(recipientsPatterns) || undefined}
              onValueChange={setRecipientsWorkflowId}
            >
              <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="選ぶ" /></SelectTrigger>
              <SelectContent>
                {recipientsPatterns.map((p) => <SelectItem key={p.workflow_id} value={p.workflow_id}>{p.label} ({p.code})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {recipients.length === 0 ? (
          <p className="text-xs text-text-sub">共有・通知先 (watcher) や条件による承認者の設定はありません。</p>
        ) : (
          <div className="divide-y divide-surface-border">
            {recipients.map((r) => (
              <div key={r.recipient_id} className="flex items-center justify-between gap-2 py-2 text-sm">
                <div>
                  <span className="text-xs font-semibold px-2 py-0.5 rounded bg-surface-sunken text-text-body mr-2">
                    {r.recipient_role === 'watcher' ? '共有・通知先' : '承認者(条件)'}
                  </span>
                  <span className="font-medium text-text-body">
                    {r.label || (r.resolution_mode === 'explicit_employee' && r.assignee_employee_id ? employeeName(r.assignee_employee_id) : '条件セットで指定')}
                  </span>
                  <span className="text-text-sub ml-2">第{r.level}段階</span>
                  {r.resolution_mode === 'explicit_employee' && r.persistence && (
                    <span className="text-text-sub ml-2">({PERSISTENCE_LABELS[r.persistence] ?? r.persistence})</span>
                  )}
                </div>
                <button type="button" aria-label="受信者設定を解除" onClick={() => revokeRecipientMut.mutate(r.recipient_id)}
                  className="p-1.5 rounded-md text-text-sub hover:text-danger-text hover:bg-surface-sunken min-h-[44px] min-w-[44px] flex items-center justify-center">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
        <div>
          <button
            type="button" onClick={() => setShowConditionEditor((v) => !v)}
            className="inline-flex items-center gap-1 text-xs text-text-sub hover:text-text-body min-h-[44px]"
          >
            {showConditionEditor ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            対象者条件 (承認者・共有先向け) を管理
          </button>
          {showConditionEditor && recipientUsagePoint && (
            <div className="mt-2">
              <ConditionSetEditor usagePointId={APPROVAL_RECIPIENT_USAGE_POINT_ID} usagePoint={recipientUsagePoint} attributeTypesById={attributeTypesById} />
            </div>
          )}
        </div>
      </div>

      {overrideForm && (
        <Modal
          open title="承認者を指名" onClose={() => setOverrideForm(null)} size="sm"
          footer={(
            <>
              <Button variant="secondary" onClick={() => setOverrideForm(null)}>キャンセル</Button>
              <Button
                variant="primary"
                disabled={!overrideForm.workflowType || !overrideForm.employeeId || overrideMaxLevels === 0 || createOverrideMut.isPending}
                loading={createOverrideMut.isPending}
                onClick={() => createOverrideMut.mutate(overrideForm)}
              >
                登録
              </Button>
            </>
          )}
        >
          <div className="space-y-3">
            {/* design-expertレビューHIGH指摘反映: max_levels=0 (ゼロ段階承認) のパターンには承認段階
                そのものが無いため、承認者を指名しても評価されない孤立設定になる。登録自体を止める。 */}
            {overrideMaxLevels === 0 && (
              <p className="text-xs text-alert-text">このパターンは承認段階が無いため (承認なし・通知のみ)、承認者を指名できません。</p>
            )}
            <div>
              <label className="block text-xs text-text-sub mb-1">対象ワークフロー</label>
              <Select
                value={overrideForm.workflowType || undefined}
                onValueChange={(v) => setOverrideForm({
                  ...overrideForm, workflowType: v,
                  workflowId: workflows.find((w) => w.workflow_type === v)?.workflow_id ?? '',
                  level: 1,
                })}
              >
                <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="選ぶ" /></SelectTrigger>
                <SelectContent>
                  {workflows.map((w) => <SelectItem key={w.workflow_id} value={w.workflow_type}>{WORKFLOW_TYPE_LABELS[w.workflow_type] ?? w.workflow_type}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {/* ⚠️ D-U22 §2-2: 複数パターンがある型のみ対象パターンを選ばせる (未選択時は既定パターン) */}
            {overridePatterns.length > 1 && (
              <div>
                <label className="block text-xs text-text-sub mb-1">対象パターン</label>
                <Select
                  value={overrideForm.workflowId || undefined}
                  onValueChange={(v) => setOverrideForm({ ...overrideForm, workflowId: v, level: 1 })}
                >
                  <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="選ぶ" /></SelectTrigger>
                  <SelectContent>
                    {overridePatterns.map((p) => (
                      <SelectItem key={p.workflow_id} value={p.workflow_id}>{p.label} ({p.code})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {/* ⚠️ D-U22 §2-2: 段階の選択肢は「選択中のパターン」の max_levels に従う
                (workflows は既定パターンの値しか持たないため、追加パターンでは食い違う) */}
            {overrideMaxLevels > 1 && (
              <div>
                <label className="block text-xs text-text-sub mb-1">段階</label>
                <Select value={String(overrideForm.level)} onValueChange={(v) => setOverrideForm({ ...overrideForm, level: Number(v) })}>
                  <SelectTrigger className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: overrideMaxLevels }, (_, i) => i + 1)
                      .map((lv) => <SelectItem key={lv} value={String(lv)}>第{lv}段階</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-text-sub mt-1">
                  第2段階以降に承認者が1人も指名されていない場合、その段階は自動的にスキップされます。
                </p>
              </div>
            )}
            <div>
              <label className="block text-xs text-text-sub mb-1">承認者に指名する社員</label>
              {employeesError && (
                <p className="text-xs text-alert-text mb-1">社員一覧の取得に失敗しました (社員管理の閲覧権限が必要です)。</p>
              )}
              <Select value={overrideForm.employeeId || undefined} onValueChange={(v) => setOverrideForm({ ...overrideForm, employeeId: v })}>
                <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="社員を選ぶ" /></SelectTrigger>
                <SelectContent>
                  {employees.map((e) => <SelectItem key={e.employee_id} value={e.employee_id}>{e.family_name} {e.given_name} ({e.employee_number})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs text-text-sub mb-1">持続性</label>
              <Select value={overrideForm.persistence} onValueChange={(v) => setOverrideForm({ ...overrideForm, persistence: v })}>
                <SelectTrigger className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="persistent">恒久 (解除するまで)</SelectItem>
                  <SelectItem value="until_reassignment">異動まで</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </Modal>
      )}

      {/* TD-27: 受信者設定の作成モーダル。⚠️ recipient_role="approver" は resolution_mode="condition_set"
          のみ許可 (§8-99/§8-122型のdead row防止、承認者の個人指名は上の「承認者の指名」セクションを使う)。 */}
      {recipientForm && (
        <Modal
          open title="受信者設定を追加" onClose={() => setRecipientForm(null)} size="sm"
          footer={(
            <>
              <Button variant="secondary" onClick={() => setRecipientForm(null)}>キャンセル</Button>
              <Button
                variant="primary"
                disabled={
                  !recipientForm.workflowType || createRecipientMut.isPending
                  || (recipientForm.resolutionMode === 'explicit_employee' ? !recipientForm.employeeId : !recipientForm.conditionSetId)
                  // design-expertレビューHIGH指摘反映: このパターンに承認段階が無い (max_levels=0) 場合、
                  // recipient_role="approver" は評価されず孤立設定になる。watcher (通知専用) は
                  // ゼロ段階承認の受理通知先としてそのまま使われるため対象外 (dispatchZeroLevelApprovalNotifications)。
                  || (recipientForm.recipientRole === 'approver' && recipientFormMaxLevels === 0)
                }
                loading={createRecipientMut.isPending}
                onClick={() => createRecipientMut.mutate(recipientForm)}
              >
                登録
              </Button>
            </>
          )}
        >
          <div className="space-y-3">
            {recipientForm.recipientRole === 'approver' && recipientFormMaxLevels === 0 && (
              <p className="text-xs text-alert-text">このパターンは承認段階が無いため (承認なし・通知のみ)、承認者 (approver) 役割の受信者は設定できません。共有・通知専用の受信者 (watcher) は設定できます。</p>
            )}
            <div>
              <label className="block text-xs text-text-sub mb-1">対象ワークフロー</label>
              <Select
                value={recipientForm.workflowType || undefined}
                onValueChange={(v) => setRecipientForm({
                  ...recipientForm, workflowType: v,
                  workflowId: workflows.find((w) => w.workflow_type === v)?.workflow_id ?? '',
                  level: 1,
                })}
              >
                <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="選ぶ" /></SelectTrigger>
                <SelectContent>
                  {workflows.map((w) => <SelectItem key={w.workflow_id} value={w.workflow_type}>{WORKFLOW_TYPE_LABELS[w.workflow_type] ?? w.workflow_type}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {/* ⚠️ D-U22 §2-2: 複数パターンがある型のみ対象パターンを選ばせる (未選択時は既定パターン) */}
            {recipientFormPatterns.length > 1 && (
              <div>
                <label className="block text-xs text-text-sub mb-1">対象パターン</label>
                <Select
                  value={recipientForm.workflowId || undefined}
                  onValueChange={(v) => setRecipientForm({ ...recipientForm, workflowId: v, level: 1 })}
                >
                  <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="選ぶ" /></SelectTrigger>
                  <SelectContent>
                    {recipientFormPatterns.map((p) => (
                      <SelectItem key={p.workflow_id} value={p.workflow_id}>{p.label} ({p.code})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {/* ⚠️ D-U22 §2-2: 段階の選択肢は「選択中のパターン」の max_levels に従う */}
            {recipientFormMaxLevels > 1 && (
              <div>
                <label className="block text-xs text-text-sub mb-1">段階</label>
                <Select value={String(recipientForm.level)} onValueChange={(v) => setRecipientForm({ ...recipientForm, level: Number(v) })}>
                  <SelectTrigger className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: recipientFormMaxLevels }, (_, i) => i + 1)
                      .map((lv) => <SelectItem key={lv} value={String(lv)}>第{lv}段階</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <label className="block text-xs text-text-sub mb-1">役割</label>
              <Select
                value={recipientForm.recipientRole}
                onValueChange={(v) => setRecipientForm({
                  ...recipientForm, recipientRole: v as 'approver' | 'watcher',
                  // 承認者は条件による指定のみ (個人指名は既存の「承認者の指名」を使う)
                  resolutionMode: v === 'approver' ? 'condition_set' : recipientForm.resolutionMode,
                })}
              >
                <SelectTrigger className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="watcher">共有・通知先 (決裁権なし)</SelectItem>
                  <SelectItem value="approver">承認者 (条件による指定)</SelectItem>
                </SelectContent>
              </Select>
              {recipientForm.recipientRole === 'approver' && (
                <p className="text-xs text-text-sub mt-1">承認者を特定の1名で指名したい場合は、上の「承認者の指名」から登録してください。</p>
              )}
            </div>
            {recipientForm.recipientRole === 'watcher' && (
              <div>
                <label className="block text-xs text-text-sub mb-1">指定方法</label>
                <Select
                  value={recipientForm.resolutionMode}
                  onValueChange={(v) => setRecipientForm({ ...recipientForm, resolutionMode: v as 'explicit_employee' | 'condition_set' })}
                >
                  <SelectTrigger className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="explicit_employee">特定の1名を指名</SelectItem>
                    <SelectItem value="condition_set">条件に一致する人を対象</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {recipientForm.resolutionMode === 'explicit_employee' ? (
              <>
                <div>
                  <label className="block text-xs text-text-sub mb-1">対象の社員</label>
                  {employeesError && (
                    <p className="text-xs text-alert-text mb-1">社員一覧の取得に失敗しました (社員管理の閲覧権限が必要です)。</p>
                  )}
                  <Select value={recipientForm.employeeId || undefined} onValueChange={(v) => setRecipientForm({ ...recipientForm, employeeId: v })}>
                    <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="社員を選ぶ" /></SelectTrigger>
                    <SelectContent>
                      {employees.map((e) => <SelectItem key={e.employee_id} value={e.employee_id}>{e.family_name} {e.given_name} ({e.employee_number})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-xs text-text-sub mb-1">持続性</label>
                  <Select value={recipientForm.persistence} onValueChange={(v) => setRecipientForm({ ...recipientForm, persistence: v })}>
                    <SelectTrigger className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="persistent">恒久 (解除するまで)</SelectItem>
                      <SelectItem value="until_reassignment">異動まで</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            ) : (
              <div>
                <label className="block text-xs text-text-sub mb-1">対象者条件</label>
                <Select value={recipientForm.conditionSetId || undefined} onValueChange={(v) => setRecipientForm({ ...recipientForm, conditionSetId: v })}>
                  <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="条件セットを選ぶ" /></SelectTrigger>
                  <SelectContent>
                    {recipientConditionSets.map((s) => <SelectItem key={s.set_id} value={s.set_id}>{s.label ?? s.set_id}</SelectItem>)}
                  </SelectContent>
                </Select>
                {recipientConditionSets.length === 0 && (
                  <p className="text-xs text-alert-text mt-1">条件セットがまだありません。下の「対象者条件を管理」から先に作成してください。</p>
                )}
              </div>
            )}
            <div>
              <label className="block text-xs text-text-sub mb-1">ラベル (任意)</label>
              <input
                value={recipientForm.label} onChange={(e) => setRecipientForm({ ...recipientForm, label: e.target.value })} maxLength={200}
                className="w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring text-sm"
              />
            </div>
          </div>
        </Modal>
      )}

      {/* D-U22 (増分①): パターンの段階数・ラベルを編集する */}
      {editingPattern && (
        <Modal
          open title="パターンを編集" onClose={() => setEditingPattern(null)} size="sm"
          footer={(
            <>
              <Button variant="secondary" onClick={() => setEditingPattern(null)}>キャンセル</Button>
              <Button
                variant="primary" disabled={!editingPattern.label.trim() || updatePatternMut.isPending}
                loading={updatePatternMut.isPending}
                onClick={() => updatePatternMut.mutate({
                  workflowId: editingPattern.workflowId, label: editingPattern.label.trim(), maxLevels: editingPattern.maxLevels,
                })}
              >
                保存
              </Button>
            </>
          )}
        >
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-text-sub mb-1">ラベル</label>
              <input
                value={editingPattern.label} onChange={(e) => setEditingPattern({ ...editingPattern, label: e.target.value })} maxLength={200}
                className="w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-text-sub mb-1">最大段階数</label>
              <Select value={String(editingPattern.maxLevels)} onValueChange={(v) => setEditingPattern({ ...editingPattern, maxLevels: Number(v) })}>
                <SelectTrigger className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[0, 1, 2, 3].map((n) => <SelectItem key={n} value={String(n)}>{formatMaxLevels(n)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {/* D-U22: 段数を下げても申請は停止しないが、進行中の申請が黙って最終承認扱いになるため警告する */}
            {patternUsage && patternUsage.max_pending_level > editingPattern.maxLevels && (
              <p className="text-xs text-alert-text flex items-start gap-1.5">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                進行中の申請{patternUsage.pending_request_count}件のうち、第{patternUsage.max_pending_level}段階まで進んでいるものは、
                次に承認されると (途中の段階が飛ばされて) 最終承認として確定します。
              </p>
            )}
          </div>
        </Modal>
      )}

      {/* 使い勝手フィードバック⑥.5 (D-U42/D-U45): ②層 (このパターン単位) の通知設定 */}
      {notificationsPattern && (
        <Modal
          open title={`通知設定 (${notificationsPattern.label})`} onClose={() => setNotificationsPattern(null)} size="lg"
          footer={<Button variant="secondary" onClick={() => setNotificationsPattern(null)}>閉じる</Button>}
        >
          <NotificationSettingsMatrix
            basePath={`/approvals/workflow-patterns/${notificationsPattern.workflow_id}/notifications`}
            canManage={canManageWorkflows}
          />
        </Modal>
      )}

      {/* D-U22 (増分①): パターンの削除確認 */}
      {deletingPattern && (
        <ConfirmDialog
          open title="パターンを削除"
          message={
            `「${deletingPattern.label}」(${deletingPattern.code}) を削除します。この操作は取り消せません。`
            + (patternUsage ? ` (進行中の申請 ${patternUsage.pending_request_count} 件 / 割当設定 ${patternUsage.bound_node_count} 件)` : '')
          }
          confirmLabel="削除する" destructive loading={deletePatternMut.isPending}
          onClose={() => setDeletingPattern(null)}
          onConfirm={() => deletePatternMut.mutate(deletingPattern.workflow_id)}
        />
      )}

      {/* D-U22 (増分①): パターンの新規作成 */}
      {creatingPatternForType && (
        <Modal
          open title="パターンを追加" onClose={() => setCreatingPatternForType(null)} size="sm"
          footer={(
            <>
              <Button variant="secondary" onClick={() => setCreatingPatternForType(null)}>キャンセル</Button>
              <Button
                variant="primary"
                disabled={!createPatternForm.code.trim() || !createPatternForm.label.trim() || createPatternMut.isPending}
                loading={createPatternMut.isPending}
                onClick={() => createPatternMut.mutate({
                  workflowType: creatingPatternForType, code: createPatternForm.code.trim(),
                  label: createPatternForm.label.trim(), maxLevels: createPatternForm.maxLevels,
                })}
              >
                作成
              </Button>
            </>
          )}
        >
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-text-sub mb-1">対象ワークフロー</label>
              <p className="text-sm text-text-body">{WORKFLOW_TYPE_LABELS[creatingPatternForType] ?? creatingPatternForType}</p>
            </div>
            <div>
              <label className="block text-xs text-text-sub mb-1">コード (半角英数字・アンダースコアのみ、&quot;default&quot;は使用不可)</label>
              <input
                value={createPatternForm.code}
                onChange={(e) => setCreatePatternForm({ ...createPatternForm, code: e.target.value })}
                maxLength={64}
                className="w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-text-sub mb-1">ラベル</label>
              <input
                value={createPatternForm.label}
                onChange={(e) => setCreatePatternForm({ ...createPatternForm, label: e.target.value })}
                maxLength={200}
                className="w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-text-sub mb-1">最大段階数</label>
              <Select value={String(createPatternForm.maxLevels)} onValueChange={(v) => setCreatePatternForm({ ...createPatternForm, maxLevels: Number(v) })}>
                <SelectTrigger className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[0, 1, 2, 3].map((n) => <SelectItem key={n} value={String(n)}>{formatMaxLevels(n)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export function ApprovalsPage() {
  const [deciding, setDeciding] = useState<ApprovalRow | null>(null);
  const { data: rows = [], isLoading, isError, refetch } = useQuery<ApprovalRow[]>({
    queryKey: ['approvals'],
    queryFn: () => apiFetch<ApprovalRow[]>('/approvals'),
  });
  const qc = useQueryClient();

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-h1">承認</h1>
        <p className="text-sm text-text-sub mt-1">自分が承認者になっている申請の一覧です。</p>
      </div>

      <WorkflowSettingsPanel />

      {isLoading ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : isError ? (
        <div className="p-6 border border-danger-border bg-danger-bg rounded-lg text-danger-text flex flex-wrap items-center justify-between gap-2">
          <span>承認一覧の取得に失敗しました。</span>
          <Button variant="secondary" size="sm" onClick={() => refetch()}>再試行</Button>
        </div>
      ) : rows.length === 0 ? (
        <EmptyState message="承認待ちの申請はありません" description="自分が承認者になっている申請があると、ここに表示されます。" />
      ) : (
        <div className="space-y-2">
          {rows.map((row) => (
            <div key={row.request_id} className="border border-surface-border rounded-lg bg-white p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-semibold px-2 py-0.5 rounded bg-surface-sunken text-text-body">
                      {WORKFLOW_TYPE_LABELS[row.workflow_type] ?? row.workflow_type}
                    </span>
                    <span className="text-xs text-text-sub">
                      {row.max_levels > 1 ? `第${row.current_level}段階 / 全${row.max_levels}段階` : ''}
                    </span>
                    {row.possible_conflict && (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded bg-alert-bg text-alert-text">
                        <AlertTriangle size={11} aria-hidden />要確認
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-text-body mt-1.5">{describePayload(row)}</p>
                  <p className="text-xs text-text-sub mt-1">
                    申請者: {row.requester_name ?? '不明'} ・ {formatDateLabel(row.created_at.slice(0, 10), { withYear: true })}
                  </p>
                  {row.possible_conflict && row.conflict_reason && (
                    <p className="text-xs text-alert-text mt-1 flex items-center gap-1">
                      <AlertTriangle size={12} aria-hidden className="shrink-0" />{row.conflict_reason}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button variant="primary" size="sm" leftIcon={<CheckCircle2 size={14} />} onClick={() => setDeciding(row)}>
                    確認して判断する
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {deciding && (
        <DecideModal
          row={deciding} onClose={() => setDeciding(null)}
          onDone={() => { setDeciding(null); qc.invalidateQueries({ queryKey: ['approvals'] }); }}
        />
      )}
    </div>
  );
}
